import { useTheme } from "@bookingcom/bui-react";
import { t, useI18n } from "@bookingcom/lingojs-react";
import { UIFlightData } from "@flights/types";
import useProfiler from "../../../../../../../hooks/useProfiler";
import useFormatPrice from "../../../../../../../hooks/useFormatPrice";
import { isOfMetaOrigin } from "../../../../../../../utils/marketing-url-params";
import web_flights_fd_genius_saving_badge_percentage from "../../../../../../../utils/experiments/genius/web_flights_fd_genius_saving_badge_percentage";

const useGeniusRewardsInfoController = (flight: UIFlightData) => {
  const theme = useTheme();
  const i18n = useI18n();
  const { isLoggedIn } = useProfiler();
  const { formatPrice } = useFormatPrice();

  const hasFlightGeniusDiscount = flight.priceBreakdown?.bcomPricingItems?.some(
    (item) => item.itemType === "GENIUS_DEAL" || item.itemType === "GENIUS_AIRLINE_FARE"
  );
  // On FlightDetails we only get one checked baggage option (the cheapest one).
  const hasCheckedBaggageDiscount =
    (flight.ancillaries?.checkedInBaggage?.options.length ?? 0) > 0 &&
    flight.ancillaries?.checkedInBaggage?.options[0].priceBreakdown?.bcomPricingItems?.some(
      (item) => item.itemType === "GENIUS_CHECKED_BAG"
    );
  const hasFlexTicketDiscount = flight.ancillaries?.flexibleTicket?.priceBreakdown?.bcomPricingItems?.some(
    (item) => item.itemType === "GENIUS_FLEXTICKET"
  );
  const hasGeniusRewards = hasFlightGeniusDiscount || hasCheckedBaggageDiscount || hasFlexTicketDiscount;

  // Using same logic as in useExtraProductsInfo.tsx.
  const showLoadingPrice = isOfMetaOrigin();

  //#region EXP web_flights_fd_genius_saving_badge_percentage
  const experimentVariant = isLoggedIn() && web_flights_fd_genius_saving_badge_percentage.trackWithDefaultStage();
  if (isLoggedIn() && hasFlightGeniusDiscount) web_flights_fd_genius_saving_badge_percentage.useTrackStages();

  let geniusSavingBadgeData:
    | { variant: "absolute"; savingsAmount: string }
    | { variant: "percentage"; percentageDiscount: number }
    | undefined;
  if (experimentVariant === 1 && flight.priceBreakdown?.totalGeniusFareDiscount) {
    // V1: Absolute amount badge
    geniusSavingBadgeData = {
      variant: "absolute",
      savingsAmount: formatPrice(flight.priceBreakdown.totalGeniusFareDiscount, { isDiscount: true })
    };
  } else if (experimentVariant === 2 && flight.priceBreakdown?.totalGeniusFareDiscountPercentage) {
    // V2: Percentage badge
    geniusSavingBadgeData = {
      variant: "percentage",
      percentageDiscount: flight.priceBreakdown.totalGeniusFareDiscountPercentage
    };
  }
  //#endregion EXP web_flights_fd_genius_saving_badge_percentage

  return {
    hasGeniusRewards,
    headerColor: theme.colors.color_brand_primary_background,
    subheader: i18n.trans(t("flights_fd_genius_rewards_subtitle")),
    checkedBaggageDiscount: hasCheckedBaggageDiscount && flight.ancillaries?.checkedInBaggage?.options[0],
    showCheckedBaggageLoadingPrice: showLoadingPrice,
    flexTicketDiscount: hasFlexTicketDiscount && flight.ancillaries?.flexibleTicket,
    flightGeniusDiscount: hasFlightGeniusDiscount && i18n.trans(t("flights_fd_lower_price")),
    geniusSavingBadgeData
  };
};

export default useGeniusRewardsInfoController;
